/**
 * Pricing Calculator Component
 * Interactive calculator for optimal pricing based on costs and margins
 */

import React, { useState, useEffect } from 'react';
import { calculateOptimalPrice, PRICING_STRATEGIES } from '../services/pricingIntelligenceService';
import { formatCurrency } from '../utils/formatters';
import './PricingCalculator.css';

const PricingCalculator = () => {
  // Inputs
  const [strategy, setStrategy] = useState(PRICING_STRATEGIES.HOURLY);
  const [directCosts, setDirectCosts] = useState('');
  const [indirectCosts, setIndirectCosts] = useState('');
  const [timeHours, setTimeHours] = useState('');
  const [desiredHourlyRate, setDesiredHourlyRate] = useState('');
  const [targetMargin, setTargetMargin] = useState(30);

  // Calculated outputs
  const [totalCosts, setTotalCosts] = useState(0);
  const [suggestedPrice, setSuggestedPrice] = useState(0);
  const [effectiveHourlyRate, setEffectiveHourlyRate] = useState(0);
  const [profitAmount, setProfitAmount] = useState(0);

  // Recalculate whenever inputs change
  useEffect(() => {
    const costs = {
      directCosts: parseFloat(directCosts) || 0,
      indirectCosts: parseFloat(indirectCosts) || 0,
      timeHours: parseFloat(timeHours) || 0,
      desiredHourlyRate: parseFloat(desiredHourlyRate) || 0,
    };

    const total = costs.directCosts + costs.indirectCosts + (costs.timeHours * costs.desiredHourlyRate);
    setTotalCosts(total);

    const price = calculateOptimalPrice(costs, targetMargin, strategy);
    setSuggestedPrice(price);

    const profit = price - total;
    setProfitAmount(profit);

    if (costs.timeHours > 0) {
      setEffectiveHourlyRate(price / costs.timeHours);
    } else {
      setEffectiveHourlyRate(0);
    }
  }, [directCosts, indirectCosts, timeHours, desiredHourlyRate, targetMargin, strategy]);

  const handleReset = () => {
    setDirectCosts('');
    setIndirectCosts('');
    setTimeHours('');
    setDesiredHourlyRate('');
    setTargetMargin(30);
    setStrategy(PRICING_STRATEGIES.HOURLY);
  };

  return (
    <div className="pricing-calculator">
      <div className="calculator-header">
        <h2>Pricing Calculator</h2>
        <p>Calculate optimal pricing based on your costs and target profit margin</p>
      </div>

      <div className="calculator-layout">
        {/* Inputs Section */}
        <div className="calculator-inputs">
          <h3>Cost Inputs</h3>

          <div className="form-group">
            <label htmlFor="calc-strategy">Pricing Strategy</label>
            <select
              id="calc-strategy"
              className="input"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
            >
              <option value={PRICING_STRATEGIES.COST_PLUS}>Cost Plus</option>
              <option value={PRICING_STRATEGIES.HOURLY}>Hourly Rate</option>
              <option value={PRICING_STRATEGIES.PROJECT}>Fixed Project</option>
              <option value={PRICING_STRATEGIES.VALUE_BASED}>Value Based</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="calc-direct">Direct Costs</label>
            <input
              id="calc-direct"
              type="number"
              className="input"
              placeholder="0.00"
              min="0"
              step="0.01"
              value={directCosts}
              onChange={(e) => setDirectCosts(e.target.value)}
            />
            <small>Software, materials, subcontractors, tools</small>
          </div>

          <div className="form-group">
            <label htmlFor="calc-indirect">Indirect Costs (Overhead)</label>
            <input
              id="calc-indirect"
              type="number"
              className="input"
              placeholder="0.00"
              min="0"
              step="0.01"
              value={indirectCosts}
              onChange={(e) => setIndirectCosts(e.target.value)}
            />
            <small>Rent, utilities, insurance, marketing</small>
          </div>

          <div className="form-group">
            <label htmlFor="calc-hours">Estimated Time (hours)</label>
            <input
              id="calc-hours"
              type="number"
              className="input"
              placeholder="0"
              min="0"
              step="0.25"
              value={timeHours}
              onChange={(e) => setTimeHours(e.target.value)}
            />
            <small>How long will this project take?</small>
          </div>

          <div className="form-group">
            <label htmlFor="calc-hourly">Desired Hourly Rate</label>
            <input
              id="calc-hourly"
              type="number"
              className="input"
              placeholder="0.00"
              min="0"
              step="0.01"
              value={desiredHourlyRate}
              onChange={(e) => setDesiredHourlyRate(e.target.value)}
            />
            <small>What you want to earn per hour</small>
          </div>

          <div className="form-group">
            <label htmlFor="calc-margin">Target Profit Margin (%)</label>
            <input
              id="calc-margin"
              type="range"
              className="slider"
              min="0"
              max="100"
              step="5"
              value={targetMargin}
              onChange={(e) => setTargetMargin(parseInt(e.target.value))}
            />
            <div className="margin-display">{targetMargin}%</div>
          </div>

          <button className="btn btn-secondary btn-block" onClick={handleReset}>
            Reset Calculator
          </button>
        </div>

        {/* Results Section */}
        <div className="calculator-results">
          <h3>Results</h3>

          <div className="result-card">
            <div className="result-label">Total Costs</div>
            <div className="result-value">{formatCurrency(totalCosts)}</div>
            <div className="result-breakdown">
              <div>Direct: {formatCurrency(parseFloat(directCosts) || 0)}</div>
              <div>Indirect: {formatCurrency(parseFloat(indirectCosts) || 0)}</div>
              <div>Labor: {formatCurrency((parseFloat(timeHours) || 0) * (parseFloat(desiredHourlyRate) || 0))}</div>
            </div>
          </div>

          <div className="result-card highlight">
            <div className="result-label">Suggested Price</div>
            <div className="result-value large">{formatCurrency(suggestedPrice)}</div>
            <div className="result-description">
              With {targetMargin}% profit margin
            </div>
          </div>

          <div className="result-card">
            <div className="result-label">Expected Profit</div>
            <div className="result-value profit">{formatCurrency(profitAmount)}</div>
            <div className="result-description">
              {totalCosts > 0 ? ((profitAmount / totalCosts) * 100).toFixed(1) : 0}% margin
            </div>
          </div>

          {timeHours > 0 && (
            <div className="result-card">
              <div className="result-label">Effective Hourly Rate</div>
              <div className="result-value">{formatCurrency(effectiveHourlyRate)}/hr</div>
              <div className="result-description">
                Based on {timeHours} hours
              </div>
            </div>
          )}

          {/* Pricing Tiers */}
          <div className="pricing-tiers">
            <h4>Alternative Pricing Options</h4>
            <div className="tier">
              <div className="tier-label">Conservative (20% margin)</div>
              <div className="tier-value">
                {formatCurrency(calculateOptimalPrice({
                  directCosts: parseFloat(directCosts) || 0,
                  indirectCosts: parseFloat(indirectCosts) || 0,
                  timeHours: parseFloat(timeHours) || 0,
                  desiredHourlyRate: parseFloat(desiredHourlyRate) || 0,
                }, 20, strategy))}
              </div>
            </div>
            <div className="tier">
              <div className="tier-label">Standard (30% margin)</div>
              <div className="tier-value">
                {formatCurrency(calculateOptimalPrice({
                  directCosts: parseFloat(directCosts) || 0,
                  indirectCosts: parseFloat(indirectCosts) || 0,
                  timeHours: parseFloat(timeHours) || 0,
                  desiredHourlyRate: parseFloat(desiredHourlyRate) || 0,
                }, 30, strategy))}
              </div>
            </div>
            <div className="tier">
              <div className="tier-label">Premium (50% margin)</div>
              <div className="tier-value">
                {formatCurrency(calculateOptimalPrice({
                  directCosts: parseFloat(directCosts) || 0,
                  indirectCosts: parseFloat(indirectCosts) || 0,
                  timeHours: parseFloat(timeHours) || 0,
                  desiredHourlyRate: parseFloat(desiredHourlyRate) || 0,
                }, 50, strategy))}
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="calculator-tips">
            <h4>Pricing Tips</h4>
            <ul>
              <li>Industry standard profit margins range from 15-40%</li>
              <li>Consider your experience level and market position</li>
              <li>Premium services can command 50%+ margins</li>
              <li>Don't forget to account for taxes and benefits</li>
              <li>Review and adjust pricing quarterly based on results</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
